import {
  Authenticator,
  createTheme,
  defaultDarkModeOverride,
  ThemeProvider,
} from '@aws-amplify/ui-react'
import { Amplify } from 'aws-amplify'
import { signOut } from 'aws-amplify/auth'
import '@aws-amplify/ui-react/styles.css'
import {
  DeleteOutlined,
  EditOutlined,
  GlobalOutlined,
  InboxOutlined,
  LogoutOutlined,
  MailOutlined,
  MoonOutlined,
  RollbackOutlined,
  SunOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { useQuery } from '@apollo/client/react'
import { ConfigProvider, Dropdown, Layout, Menu, Spin, theme } from 'antd'
import { Suspense, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useThemeMode } from '~app/components/GlobalContainer/ThemeModeProvider'
import { config } from '~app/config'
import { MeDocument } from '~app/graph/generated/graphql'
import { mainBlue, white } from '~app/helper/Colors'
import i18n from '~app/i18n/i18n'
import logoMark from '~app/images/logomark_white.png'
import { AbsoluteRoutes } from '~app/interfaces/routes'
import '~app/style/App.css'

const { Header, Content } = Layout

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: config.UserPoolId,
      userPoolClientId: config.ClientId,
    },
  },
})

// Holds the Me query and everything keyed off it. It renders only as a child
// of <Authenticator>, so the query fires after sign-in with a valid token —
// not on the unauthenticated page load, where it would resolve to an empty
// `me` that cache-first then pins until a hard refresh (empty header menu).
const AuthenticatedShell = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { effectiveMode, setMode } = useThemeMode()
  const { token } = theme.useToken()
  const { data: meData } = useQuery(MeDocument, { fetchPolicy: 'cache-first' })
  const groups = meData?.me?.groups || []

  const [activeNavItem, setActiveNavItem] = useState(location.pathname)

  useEffect(() => {
    setActiveNavItem(location.pathname)
  }, [location.pathname])

  const languageMenuProps = {
    onClick: ({ key }: { key: string }) => {
      i18n.changeLanguage(key)
    },
    selectedKeys: [i18n.language],
    items: [
      { key: 'en', label: 'English' },
      { key: 'de', label: 'Deutsch' },
    ],
  }

  const handleLogout = async () => {
    await signOut()
    window.location.reload()
  }

  const menuItems: React.ComponentProps<typeof Menu>['items'] = []

  if (groups.length) {
    menuItems.push({
      key: AbsoluteRoutes.home,
      icon: <InboxOutlined />,
      label: t('titles.appTitle'),
    })

    menuItems.push({
      key: AbsoluteRoutes.filesWithErrors,
      icon: <WarningOutlined />,
      label: t('navigationDrawer.filesWithErrors'),
    })

    if (groups.includes('Admins')) {
      menuItems.push({
        key: AbsoluteRoutes.returns,
        icon: <RollbackOutlined />,
        label: t('titles.returnsTitle'),
      })

      menuItems.push({
        key: AbsoluteRoutes.sources,
        icon: <MailOutlined />,
        label: t('titles.forwardsTitle'),
      })

      menuItems.push({
        key: AbsoluteRoutes.emailUpdate,
        icon: <EditOutlined />,
        label: t('navigationDrawer.changeCustomerEmail'),
      })

      menuItems.push({
        key: AbsoluteRoutes.document,
        icon: <DeleteOutlined />,
        label: t('navigationDrawer.deleteDocuments'),
      })

      menuItems.push({
        key: AbsoluteRoutes.customerDetails,
        icon: <UserOutlined />,
        label: t('navigationDrawer.customerDetails'),
      })
    }
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header
        style={{
          background: mainBlue,
          padding: '0 16px',
          display: 'flex',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 1,
          height: 48,
          lineHeight: '48px',
        }}
      >
        <img
          src={logoMark}
          alt="Logo"
          style={{ height: 28, marginRight: 16 }}
        />
        <ConfigProvider
          theme={{
            components: {
              Menu: {
                horizontalItemSelectedColor: mainBlue,
                horizontalItemSelectedBg: white,
                horizontalItemHoverColor: white,
                itemColor: 'rgba(255, 255, 255, 0.75)',
                activeBarHeight: 0,
                itemBorderRadius: 6,
                itemHoverColor: white,
              },
            },
          }}
        >
          <Menu
            mode="horizontal"
            selectedKeys={[activeNavItem]}
            onSelect={({ key }) => {
              navigate(key)
              setActiveNavItem(key)
            }}
            items={menuItems}
            style={{
              flex: 1,
              background: 'transparent',
              borderBottom: 'none',
              lineHeight: '48px',
            }}
          />
        </ConfigProvider>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            className="header-control"
            style={{ fontSize: token.fontSize }}
            onClick={() => setMode(effectiveMode === 'dark' ? 'light' : 'dark')}
          >
            {effectiveMode === 'dark' ? <SunOutlined /> : <MoonOutlined />}
            {effectiveMode === 'dark'
              ? t('labels.lightMode')
              : t('labels.darkMode')}
          </span>
          <Dropdown menu={languageMenuProps}>
            <span
              className="header-control"
              style={{ fontSize: token.fontSize }}
            >
              <GlobalOutlined />
              {i18n.language.slice(0, 2).toUpperCase()}
            </span>
          </Dropdown>
          <span
            className="header-control"
            style={{ fontSize: token.fontSize }}
            onClick={handleLogout}
          >
            <LogoutOutlined />
            {t('labels.logout')}
          </span>
        </div>
      </Header>
      <Content style={{ padding: 20, overflowX: 'auto' }}>
        <Suspense
          fallback={
            <Spin
              size="large"
              style={{ display: 'block', margin: '100px auto' }}
            />
          }
        >
          <Outlet />
        </Suspense>
      </Content>
    </Layout>
  )
}

/** Caya's mainBlue as an Amplify primary colour scale. */
const cayaBluePrimary = {
  10: { value: '#e6f6fe' },
  20: { value: '#b3e4fc' },
  40: { value: '#66c4f8' },
  60: { value: '#33b0f5' },
  80: { value: mainBlue },
  90: { value: '#0086d1' },
  100: { value: '#006cae' },
}

/**
 * Amplify theme for the Authenticator. The base tokens brand it with Caya's
 * mainBlue in both modes (button + interactive links); Amplify ships no dark
 * CSS, so defaultDarkModeOverride supplies the dark palette, which we then tint
 * to the Caya dark surfaces. The Authenticator follows the app's resolved mode.
 */
const amplifyTheme = createTheme({
  name: 'caya-mailroom',
  tokens: {
    colors: {
      // The primary button reads colors.primary; links read font.interactive.
      // Point both (and brand.primary) at Caya's mainBlue so they match the nav.
      primary: cayaBluePrimary,
      brand: { primary: cayaBluePrimary },
      font: {
        interactive: { value: mainBlue },
      },
    },
  },
  overrides: [
    defaultDarkModeOverride,
    {
      colorMode: 'dark',
      tokens: {
        colors: {
          // defaultDarkModeOverride remaps colors.primary, so re-assert Caya blue.
          primary: cayaBluePrimary,
          background: {
            primary: { value: '#1f1f1f' },
            secondary: { value: '#141414' },
            tertiary: { value: '#2a2a2a' },
          },
          font: {
            primary: { value: 'rgba(255, 255, 255, 0.88)' },
            secondary: { value: 'rgba(255, 255, 255, 0.65)' },
            interactive: { value: mainBlue },
          },
          border: {
            primary: { value: '#424242' },
            secondary: { value: '#303030' },
          },
        },
      },
    },
  ],
})

const App = () => {
  const { effectiveMode } = useThemeMode()

  return (
    <ThemeProvider theme={amplifyTheme} colorMode={effectiveMode}>
      <Authenticator hideSignUp={true}>
        <AuthenticatedShell />
      </Authenticator>
    </ThemeProvider>
  )
}

export default App
